import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {serverLocalName} from 'GLOBAL/envVariables'
import {getAllOfficeList} from 'utils/apiCalling/api'
import useOfficeHierarchy from 'utils/hooks/useOfficeHierarchy'
import {
  ALL_OFFICES,
  PHOTO_CONFIG_PATH,
  PhotoConfig,
  PhotoOffice,
  defaultPhotoConfig,
  getPhotoConfigStatus,
  loadPhotoConfig,
  parsePhotoConfig,
  stringifyPhotoConfig,
} from 'utils/config/photoConfig'
import {normalAlert, resultAlert} from 'utils/CustomAlert/Alert'

/** nginx .htpasswd 에 등록한 계정명. 비밀번호는 코드에 두지 않는다. */
const CONFIG_AUTH_USER = 'admin'

const PhotoConfigPage = () => {
  const officeInfo = useSelector((state: any) => state?.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const [officeList, setOfficeList] = useState<any[]>([])
  const [offices, setOffices] = useState<PhotoOffice[]>([])
  /* 검색어를 직접 들고 있어야 X 가 선택까지 지우지 않게 막을 수 있다 */
  const [officeSearch, setOfficeSearch] = useState('')
  const [status, setStatus] = useState(getPhotoConfigStatus())
  const [saving, setSaving] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [password, setPassword] = useState('')
  /* 저장은 nginx Basic 인증을 통과해야 한다.
     비밀번호를 코드나 localStorage 에 두면 인증 의미가 없으므로,
     저장할 때 입력받아 같은 화면에 머무는 동안만 메모리에 들고 있는다. */
  const credentialRef = useRef<string | null>(null)

  const refresh = async () => {
    const loaded = await loadPhotoConfig()
    setOffices(loaded.offices.map((office) => ({...office})))
    setStatus(getPhotoConfigStatus())
  }

  useEffect(() => {
    refresh()
    /* 사업소를 코드로 외우게 하지 않으려면 목록이 필요하다.
       목록을 못 받아도 저장된 코드는 그대로 보이므로 편집 자체는 막지 않는다. */
    getAllOfficeList(userInfo, {
      success: (res: any) => setOfficeList(res ?? []),
      fail: () => setOfficeList([]),
    })
  }, [])

  /* `전체` 라는 이름의 항목은 계층 계산에서 빠지고 목록 맨 위에 고정된다(officeHierarchy 규칙) */
  const officeOptions = useMemo(
    () => [{officeId: ALL_OFFICES, officeName: '전체'}, ...officeList],
    [officeList]
  )

  /** 사업소 코드로도 검색되도록 표시 문자열을 그대로 검색 대상으로 넘긴다 */
  const officeOptionLabel = (option: any) =>
    !option || option.officeId === ALL_OFFICES
      ? '전체'
      : `${option.officeName ?? ''} (${option.officeId ?? ''})`

  const officeHierarchy = useOfficeHierarchy(officeOptions, {getLabel: officeOptionLabel})

  /**
   * 선택된 값. 목록에 없는 코드(다른 서버에서 만든 설정 등)도 값을 잃지 않도록
   * 저장된 코드를 그대로 항목처럼 만들어 돌려준다.
   */
  const selected = offices.map(
    (office) =>
      officeOptions.find((option: any) => option.officeId === office.officeId) ?? {
        officeId: office.officeId,
        officeName: office.label || '목록에 없는 사업소',
      }
  )

  const handleChange = (values: any[]) =>
    setOffices(
      values.map((value) => ({
        officeId: String(value?.officeId ?? ''),
        label: value?.officeId === ALL_OFFICES ? '전체' : String(value?.officeName ?? ''),
      }))
    )

  /** 지금 접속한 사업소가 사진 모달을 여는지. 화면과 실제 판정이 어긋나면 안 된다. */
  const currentOfficeID = String(officeInfo?.value ?? '')
  /* `전체` 조회는 사업소 코드가 아니라 'all' 이라 어떤 설정에도 걸리지 않는다 */
  const isAllOfficeView = currentOfficeID === 'all' || currentOfficeID === 'yit'
  const currentIsPhoto = offices.some(
    (office) =>
      office.officeId === ALL_OFFICES ||
      (office.officeId !== '' && currentOfficeID.startsWith(office.officeId))
  )

  /** 저장 전 검증. 화면에서 만든 값이라도 중복 같은 실수는 걸러야 한다. */
  const validate = (): string | null => {
    const config: PhotoConfig = {offices}
    const parsed = parsePhotoConfig(JSON.parse(stringifyPhotoConfig(config)))
    return typeof parsed === 'string' ? parsed : null
  }

  const download = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    const blob = new Blob([stringifyPhotoConfig({offices})], {type: 'application/json'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'photo-config.json'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** 저장 버튼. 비밀번호가 아직 없으면 먼저 입력받는다. */
  const requestSave = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    if (!credentialRef.current) {
      setAuthOpen(true)
      return
    }
    save()
  }

  const confirmPassword = () => {
    try {
      credentialRef.current = btoa(`${CONFIG_AUTH_USER}:${password}`)
    } catch {
      /* btoa 는 한글 등 비 ASCII 를 처리하지 못한다 */
      normalAlert('비밀번호는 영문·숫자·기호만 사용할 수 있습니다.')
      return
    }
    setPassword('')
    setAuthOpen(false)
    save()
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await fetch(PHOTO_CONFIG_PATH, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${credentialRef.current}`,
        },
        body: stringifyPhotoConfig({offices}),
      })

      if (res.status === 401) {
        /* 잘못된 비밀번호를 계속 들고 있으면 다음 저장도 막히므로 버린다 */
        credentialRef.current = null
        await normalAlert('비밀번호가 올바르지 않습니다. 다시 시도해주세요.')
        return
      }
      if (res.status === 403) {
        /* 403 은 인증은 통과했는데 쓰기가 거부된 것이다. 비밀번호를 다시 묻게 하면
           맞는 비밀번호를 계속 의심하게 되므로 원인을 그대로 알려준다. */
        await normalAlert(
          '서버가 이 파일 쓰기를 거부했습니다. (HTTP 403)\n비밀번호 문제가 아니라 저장 경로 권한 설정을 확인해야 합니다.'
        )
        return
      }
      if (!res.ok) {
        await normalAlert(
          `저장하지 못했습니다. (HTTP ${res.status})\n서버에 쓰기 설정이 되어 있는지 확인해주세요.`
        )
        return
      }

      /* PUT 응답만 믿지 않고 다시 읽어 실제 반영을 확인한다 */
      await refresh()
      await resultAlert({resultCode: 0})
    } catch {
      await normalAlert('저장 요청에 실패했습니다. 네트워크 상태를 확인해주세요.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={'card card-body'}>
      {/* 서버에 직접 접속할 수 없는 환경이라, 지금 무엇이 적용 중인지 화면이 말해줘야 한다 */}
      {status.source === 'remote' ? (
        <Alert severity={'success'} className={'mb-4 fs-5'}>
          설정 파일이 적용 중입니다.
          {status.loadedAt && ` (확인 시각 ${status.loadedAt.toLocaleString('ko-KR')})`}
        </Alert>
      ) : (
        <Alert severity={'warning'} className={'mb-4 fs-5'}>
          기본값으로 동작 중입니다. {status.error ?? '설정 파일이 없습니다.'}
        </Alert>
      )}

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-1'}>행을 클릭하면 사진 모달을 여는 사업소</h3>
        <p className={'text-muted fs-6 mb-1'}>
          수용가 정보·현황·검침 등 목록에서 행을 눌렀을 때 무엇이 열릴지 정합니다.
        </p>
        <ul className={'text-muted fs-6'}>
          <li>
            목록에 <b>있으면</b> → 사진 모달 (사진첩 배지도 함께 표시됩니다)
          </li>
          <li>
            목록에 <b>없으면</b> → 수용가 상세 모달
          </li>
        </ul>
      </div>

      <div className={'mb-5'}>
        <Autocomplete
          multiple
          /* 여러 곳을 이어서 고를 때 목록이 닫히면 매번 다시 열어야 한다 */
          disableCloseOnSelect
          options={officeHierarchy.options}
          filterOptions={officeHierarchy.filterOptions}
          renderOption={officeHierarchy.renderOption}
          getOptionLabel={officeOptionLabel}
          isOptionEqualToValue={(option: any, value: any) => option?.officeId === value?.officeId}
          value={selected}
          inputValue={officeSearch}
          /* 포커스가 빠져도 검색어를 지우지 않는다 (아래 칩을 지우다 돌아오는 경우가 많다) */
          clearOnBlur={false}
          onInputChange={(_, next, reason) => {
            /* MUI 는 항목을 고를 때마다 검색어를 비운다(reset). "진주"로 검색해
               상수·지하수를 이어서 고르는 식이라 검색어를 유지한다.
               X 로 지우는 것(clear)과 직접 타이핑(input)만 반영한다. */
            if (reason === 'reset') return
            setOfficeSearch(next)
          }}
          onChange={(_, values: any[], reason) => {
            /* X 는 검색어만 지운다. MUI 기본 동작은 고른 사업소까지 함께 비우는데,
               선택은 아래 목록의 칩에서 지우는 편이 실수가 적다. */
            if (reason === 'clear') return
            handleChange(values)
          }}
          /* 지울 검색어가 없으면 X 를 감춘다 — 눌러도 할 일이 없고,
             선택을 지우는 버튼으로 오해하기 쉽다. */
          forcePopupIcon
          clearIcon={officeSearch === '' ? null : undefined}
          noOptionsText={'검색 결과가 없습니다.'}
          /**
           * 키보드로 선택이 바뀌지 않게 막는다. 여기서 preventDefault 를 하면
           * MUI 의 기본 키 처리가 건너뛰어진다(우리 핸들러가 먼저 실행된다).
           *
           * - Backspace: 검색어가 비어 있을 때 마지막 선택을 지우는 것이 기본 동작인데,
           *   칩이 입력창 밖에 있어 **보이지 않는 곳에서 사업소가 사라진다.**
           * - Enter: 선택은 목록을 눈으로 확인하고 누르게 한다.
           *
           * 검색어가 있을 때의 Backspace 는 그대로 글자를 지운다.
           */
          onKeyDown={(event) => {
            if (event.key === 'Enter' || (event.key === 'Backspace' && officeSearch === '')) {
              event.preventDefault()
            }
          }}
          /* 검색창은 짧아도 되는데 화면 폭을 다 쓰면 오른쪽이 비어 허전하다.
             좁은 화면에서는 다시 꽉 채운다. */
          sx={{width: {xs: '100%', sm: '40%'}, minWidth: {sm: 320}, maxWidth: 560}}
          /* 고른 사업소는 아래 목록에서 따로 보여준다.
             많아지면 입력창이 세로로 늘어나 검색창을 찾기 어려워지기 때문이다. */
          renderTags={() => null}
          renderInput={(params) => (
            <TextField {...params} label={'사업소 검색'} placeholder={'이름 또는 코드로 검색'} />
          )}
        />
        <p className={'text-muted fs-6 mt-2 mb-0'}>
          본부를 고르면 그 아래 사업소까지 함께 적용됩니다(하위 코드가 상위 코드로 시작하므로).
          <code className={'ms-1'}>전체</code>를 고르면 이 서버의 모든 사업소가 사진 모달을 엽니다.
        </p>
      </div>

      <div className={'border rounded p-4 mb-5'}>
        <div className={'d-flex align-items-center justify-content-between mb-3'}>
          <h4 className={'fw-bold fs-5 mb-0'}>선택된 사업소 {selected.length}곳</h4>
          {selected.length > 0 && (
            <Button size={'small'} color={'inherit'} onClick={() => setOffices([])}>
              모두 해제
            </Button>
          )}
        </div>

        {selected.length === 0 ? (
          <p className={'text-muted fs-6 mb-0'}>
            선택된 사업소가 없습니다. 이대로 저장하면 <b>모든 사업소</b>에서 행을 클릭했을 때
            수용가 상세 모달이 열립니다.
          </p>
        ) : (
          <div className={'d-flex flex-wrap gap-2'}>
            {selected.map((option: any) => (
              <Chip
                key={option.officeId}
                label={officeOptionLabel(option)}
                onDelete={() =>
                  setOffices((prev) => prev.filter((office) => office.officeId !== option.officeId))
                }
              />
            ))}
          </div>
        )}
      </div>

      <Alert severity={'info'} className={'mb-5 fs-6'}>
        {isAllOfficeView ? (
          <>
            사업소를 <b>전체</b>로 조회하는 동안에는 위 목록과 무관하게 항상{' '}
            <b>수용가 상세 모달</b>이 열립니다. 목록에 진주 수용가가 섞여 있어도 마찬가지입니다 —
            판정이 행이 아니라 선택된 사업소를 기준으로 하기 때문입니다.
          </>
        ) : (
          <>
            지금 접속한 사업소 <b>{officeInfo?.value ?? '-'}</b> ({serverLocalName} 서버) 는 행 클릭
            시 <b>{currentIsPhoto ? '사진 모달' : '수용가 상세 모달'}</b>이 열립니다.
          </>
        )}
      </Alert>

      <div className={'d-flex flex-wrap gap-3 border-top pt-4'}>
        <Button variant={'contained'} onClick={requestSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
        <Button variant={'outlined'} onClick={download}>
          JSON 다운로드
        </Button>
        <Button variant={'outlined'} color={'inherit'} onClick={refresh}>
          다시 불러오기
        </Button>
        <Button
          variant={'text'}
          color={'inherit'}
          onClick={() =>
            setOffices(defaultPhotoConfig(serverLocalName).offices.map((office) => ({...office})))
          }
        >
          기본값으로 되돌리기
        </Button>
      </div>

      <Dialog open={authOpen} onClose={() => setAuthOpen(false)} fullWidth maxWidth={'xs'}>
        <DialogTitle className={'fw-bold'}>저장 비밀번호</DialogTitle>
        <DialogContent>
          <p className={'text-muted fs-6 mb-3'}>
            설정을 저장하려면 비밀번호가 필요합니다. 이 화면을 벗어나면 다시 입력해야 합니다.
          </p>
          <TextField
            autoFocus
            fullWidth
            type={'password'}
            label={'비밀번호'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmPassword()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color={'inherit'} onClick={() => setAuthOpen(false)}>
            취소
          </Button>
          <Button variant={'contained'} onClick={confirmPassword} disabled={password === ''}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default PhotoConfigPage
